// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { FieldChangesMapPublic } from '../../../../../../../../common/types/fields/field-changes';
import { useCurrentFormState } from '../../../../../../../../controllers/issue-adjustments/hooks/use-current-form-state';
import { useFormMetadata } from '../../../../../../../../controllers/issue-adjustments/hooks/use-form-metadata';
import { useViewType } from '../../../../../../../../controllers/issue-adjustments/hooks/use-view-type';
import { UiModificationsFieldsValidationError } from '../../../../../../../../controllers/issue-adjustments/utils/errors/ui-modifications-field-validation-error';

import { validateFieldChanges } from './validate-field-changes';
import type {
	FieldsChangesValidationResult,
	InvalidFieldValidationResult,
} from './validate-field-changes/types';

const filterInvalidResults = (
	results: FieldsChangesValidationResult,
): InvalidFieldValidationResult[] =>
	results.reduce((acc: InvalidFieldValidationResult[], result) => {
		if (result.isValid === false) {
			acc.push(result);
		}
		return acc;
	}, []);

const mapValidResultsToFieldChangesMap = (results: FieldsChangesValidationResult) =>
	results.reduce((acc: FieldChangesMapPublic, result) => {
		if (result.isValid === true) {
			const {
				data: { fieldId, fieldData },
			} = result;
			acc[fieldId] = fieldData;
		}
		return acc;
	}, {});

export const useFieldsChangesValidation = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const formMetadata = useFormMetadata();
	const formMetadataRef = useRef(formMetadata);
	const viewType = useViewType();
	const { currentFormState } = useCurrentFormState();
	const currentFormStateRef = useRef(currentFormState);

	/**
	 * We want to have an up-to-date data when this hook's
	 * returned function is being called in useSubmit callback function
	 * thats why we keep it in a ref object
	 */
	formMetadataRef.current = formMetadata;
	currentFormStateRef.current = currentFormState;

	return useCallback(
		async (fieldsChanges: FieldChangesMapPublic): Promise<FieldChangesMapPublic> => {
			const fieldsChangesValidationResult: FieldsChangesValidationResult = await Promise.all(
				Object.entries(fieldsChanges)
					.filter(([fieldId]) => {
						return formMetadataRef.current[fieldId] !== undefined;
					})
					.map(async ([fieldId, fieldData]) => {
						const { fieldType } = formMetadataRef.current[fieldId];

						const fieldValidationResult = await validateFieldChanges({
							fieldType,
							fieldId,
							viewType,
							createAnalyticsEvent,
							fieldMetadata: formMetadataRef.current[fieldId],
							currentField: currentFormStateRef.current[fieldId],
							fieldFromIframe: fieldData,
						});

						if (fieldValidationResult.isValid === false) {
							return fieldValidationResult;
						}

						return {
							...fieldValidationResult,
							data: {
								fieldId,
								fieldData,
							},
						};
					}),
			);

			const failedValidationResults = filterInvalidResults(fieldsChangesValidationResult);
			if (failedValidationResults.length) {
				throw new UiModificationsFieldsValidationError('Validation failed', {
					errors: failedValidationResults.flatMap((result) => result.errors),
				});
			}

			return mapValidResultsToFieldChangesMap(fieldsChangesValidationResult);
		},
		[createAnalyticsEvent, viewType],
	);
};
